import JsPDF from 'jspdf';
import moment from 'moment';
import { PdfPage } from 'components/PdfExportConfigDialog/hooks/types';
import { extractDate, ISO_DATE_FORMAT, Logger } from 'utilities';
import {
  CONVERSION_RATIOS,
  FOOTER_DATA_LAYOUT,
  IMAGE_SETTINGS,
  SCALAR_OWNED_COVER_DISCLAIMER,
  TEMPLATE_CONSTANTS,
} from './constants';
import { CapturedImage, ExportFooterData, PdfExportConfig, PdfExportResult } from './interfaces';

// Approximate conversion from a font size in points to a comfortable line
// height in millimeters (1pt ≈ 0.3528mm, with ~1.25 leading).
const ptToLineHeightMm = (fontSizePt: number): number => fontSizePt * 0.3528 * 1.25;

// Approximate cap height in millimeters for a font size in points (1pt ≈ 0.3528mm,
// with Inter's cap height at ~0.7em). Used to optically center text on a rule.
const ptToCapHeightMm = (fontSizePt: number): number => fontSizePt * 0.3528 * 0.7;

/**
 * PDF Exporter class for handling multiple pages export
 */
export class PdfExporter {
  private pdf: JsPDF | null = null;
  private pageLogo: string | null = null;
  private coverLogoDataUrl: string | null = null;
  private infoIcon: string | null = null;

  /**
   * Static cache to store image data URLs by their source path.
   * Prevents redundant network requests and base64 conversions.
   */
  private static imageCache: Map<string, Promise<string>> = new Map();

  // Streaming state. The capture pipeline calls initDocument() once, then
  // addCapturedPage() for each captured page (in input order), then finalize().
  // Footer rendering is deferred to finalize() because it needs the final
  // included-page count for the "Page X of Y" stamp.
  private renderConfig: PdfExportConfig | null = null;
  private addedPages: { link: PdfPage }[] = [];

  /**
   * Load image from public folder and convert to base64 data URL
   */
  private static async loadImageAsDataUrl(imagePath: string): Promise<string> {
    if (this.imageCache.has(imagePath)) {
      return this.imageCache.get(imagePath)!;
    }

    const promise = (async () => {
      try {
        const response = await fetch(imagePath);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} fetching ${imagePath}`);
        }
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        Logger.error('Error loading image:', error);
        // Remove from cache on failure so we can retry later
        this.imageCache.delete(imagePath);
        throw error;
      }
    })();

    this.imageCache.set(imagePath, promise);
    return promise;
  }

  /**
   * Load an image source only when one is configured. An empty source is a
   * legitimate state -- a firm-owned export whose firm has no logo ships no page
   * logo -- so it resolves to null instead of fetching an empty URL, which would
   * resolve to the app's own HTML and later crash jspdf's addImage.
   */
  private static loadOptionalImageAsDataUrl(imagePath: string): Promise<string | null> {
    return imagePath ? PdfExporter.loadImageAsDataUrl(imagePath) : Promise.resolve(null);
  }

  /**
   * Infer jspdf-compatible format string ('png' | 'jpeg') from a data URL or
   * file path. Falls back to PNG for unknown/unrecognized inputs.
   */
  private static getImageFormat(src: string): typeof IMAGE_SETTINGS.PNG_FORMAT | typeof IMAGE_SETTINGS.JPEG_FORMAT {
    if (src.startsWith('data:image/jpeg') || src.startsWith('data:image/jpg')) {
      return IMAGE_SETTINGS.JPEG_FORMAT;
    }
    if (/\.(jpe?g)(\?|$)/i.test(src)) {
      return IMAGE_SETTINGS.JPEG_FORMAT;
    }
    return IMAGE_SETTINGS.PNG_FORMAT;
  }

  /**
   * Get image dimensions from data URL
   */
  private static async getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Convert Blob to binary string for jsPDF
   */
  private static blobToBinaryString(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(binary);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  private static async loadCustomFonts(pdf: JsPDF): Promise<void> {
    try {
      // Load Inter Regular
      const interRegularResponse = await fetch('/fonts/Inter-Regular.ttf');
      if (!interRegularResponse.ok) {
        throw new Error(`Failed to load Inter-Regular.ttf: ${interRegularResponse.statusText}`);
      }
      const interRegularBlob = await interRegularResponse.blob();
      const interRegularBinary = await PdfExporter.blobToBinaryString(interRegularBlob);

      pdf.addFileToVFS('Inter-Regular.ttf', interRegularBinary);
      pdf.addFont('Inter-Regular.ttf', 'Inter', 'normal');

      // Load Inter Italic
      const interItalicResponse = await fetch('/fonts/Inter-Italic.ttf');
      if (!interItalicResponse.ok) {
        throw new Error(`Failed to load Inter-Italic.ttf: ${interItalicResponse.statusText}`);
      }
      const interItalicBlob = await interItalicResponse.blob();
      const interItalicBinary = await PdfExporter.blobToBinaryString(interItalicBlob);

      pdf.addFileToVFS('Inter-Italic.ttf', interItalicBinary);
      pdf.addFont('Inter-Italic.ttf', 'Inter', 'italic');

      // Load Inter ExtraLight
      const interExtraLightResponse = await fetch('/fonts/Inter-ExtraLight.ttf');
      if (!interExtraLightResponse.ok) {
        throw new Error(`Failed to load Inter-ExtraLight.ttf: ${interExtraLightResponse.statusText}`);
      }
      const interExtraLightBlob = await interExtraLightResponse.blob();
      const interExtraLightBinary = await PdfExporter.blobToBinaryString(interExtraLightBlob);
      pdf.addFileToVFS('Inter-ExtraLight.ttf', interExtraLightBinary);
      pdf.addFont('Inter-ExtraLight.ttf', 'Inter', 'extra-light');

      // Load Inter Bold
      const interBoldResponse = await fetch('/fonts/Inter-Bold.ttf');
      if (interBoldResponse.ok) {
        const interBoldBlob = await interBoldResponse.blob();
        const interBoldBinary = await PdfExporter.blobToBinaryString(interBoldBlob);
        pdf.addFileToVFS('Inter-Bold.ttf', interBoldBinary);
        pdf.addFont('Inter-Bold.ttf', 'Inter', 'bold');
      }

      // Load Neuton Light
      const neutonResponse = await fetch('/fonts/Neuton-Light.ttf');
      if (!neutonResponse.ok) {
        throw new Error(`Failed to load Neuton-Light.ttf: ${neutonResponse.statusText}`);
      }
      const neutonBlob = await neutonResponse.blob();
      const neutonBinary = await PdfExporter.blobToBinaryString(neutonBlob);
      pdf.addFileToVFS('Neuton-Light.ttf', neutonBinary);
      pdf.addFont('Neuton-Light.ttf', 'Neuton', 'light');

      // Load Neuton Italic
      const neutonItalicResponse = await fetch('/fonts/Neuton-Italic.ttf');
      if (!neutonItalicResponse.ok) {
        throw new Error(`Failed to load Neuton-Italic.ttf: ${neutonItalicResponse.statusText}`);
      }
      const neutonItalicBlob = await neutonItalicResponse.blob();
      const neutonItalicBinary = await PdfExporter.blobToBinaryString(neutonItalicBlob);
      pdf.addFileToVFS('Neuton-Italic.ttf', neutonItalicBinary);
      pdf.addFont('Neuton-Italic.ttf', 'Neuton', 'italic');

      Logger.info('Custom fonts loaded successfully');
    } catch (error) {
      Logger.warn('Failed to load custom fonts', error);
    }
  }

  /**
   * Generate cover page template
   */
  public async generateCover(config: PdfExportConfig): Promise<void> {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const {
      coverBackgroundImage,
      coverTitle,
      entityName,
      issueDate,
      coverLogo,
      coverSecondaryLogo,
      firmName,
      isFirmOwned,
      isScalarOwnedValuation,
      measurementDate,
      showScalarBranding = true,
    } = config;

    // Background image
    this.pdf.addImage(
      coverBackgroundImage,
      IMAGE_SETTINGS.JPEG_FORMAT,
      0,
      0,
      this.pdf.internal.pageSize.getWidth(),
      this.pdf.internal.pageSize.getHeight()
    );

    // Use the pre-loaded data URL so jspdf receives the decoded bytes; detect
    // the actual format to avoid the 'wrong PNG signature' crash when the firm
    // logo is a JPEG. Never fall back to a raw URL/path — jspdf cannot fetch
    // external URLs and will throw "wrong PNG signature" on the raw string bytes.
    const coverLogoSrc = this.coverLogoDataUrl;
    const coverLogoFormat = coverLogoSrc ? PdfExporter.getImageFormat(coverLogoSrc) : IMAGE_SETTINGS.PNG_FORMAT;

    // Logo image setup — only measure dimensions when we have a valid data URL.
    const logoHeightMm = coverLogoSrc
      ? (await PdfExporter.getImageDimensions(coverLogoSrc)).height / CONVERSION_RATIOS.MM_TO_PX
      : 0;
    const pageHeight = this.pdf.internal.pageSize.getHeight();
    const pageWidth = this.pdf.internal.pageSize.getWidth();

    if (isFirmOwned) {
      // Cover brand slot (top left): the firm's own logo, or the firm name when the firm
      // has no logo. Never Scalar's logo — it would misattribute a firm-owned valuation.
      // Mirrors the same fallback in generateHeader.
      const brandSlotCenterY
        = TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM / 2;
      let brandSlotWidthMm = 0;

      if (coverLogoSrc) {
        this.pdf.addImage(
          coverLogoSrc,
          coverLogoFormat,
          TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
          TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
        );
        brandSlotWidthMm = TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM;
      } else if (firmName) {
        this.pdf.setFont('Inter', 'bold');
        this.pdf.setFontSize(TEMPLATE_CONSTANTS.COVER_FIRM_NAME_FONT_SIZE_PT);
        this.pdf.setTextColor(3, 125, 232);
        const firmNameText = firmName.toUpperCase();
        // Nudge the baseline down by half the cap height so the accent line runs through
        // the name's vertical middle, the way it crosses the logo's mid-height.
        const capHalfHeightMm = ptToCapHeightMm(TEMPLATE_CONSTANTS.COVER_FIRM_NAME_FONT_SIZE_PT) / 2;
        this.pdf.text(firmNameText, TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM, brandSlotCenterY + capHalfHeightMm);
        brandSlotWidthMm = this.pdf.getTextWidth(firmNameText);
      }

      // Draw a horizontal line to the right of the brand slot, centered vertically, spanning to page margin
      const lineXStart =
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM +
        brandSlotWidthMm +
        TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      // The brand slot is variable width when it holds a name, so skip the line rather
      // than drawing it backwards for a firm name long enough to reach the page margin.
      if (lineXEnd > lineXStart) {
        this.pdf.line(lineXStart, brandSlotCenterY, lineXEnd, brandSlotCenterY);
      }
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);

      // Bottom rule spanning the full width between the cover margins.
      const bottomLineY =
        pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_BOTTOM_LINE_INSET_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      this.pdf.line(
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
        bottomLineY,
        pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
        bottomLineY
      );
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);
    } else {
      // Bottom left position — only rendered when the logo loaded successfully as a data URL.
      if (coverLogoSrc && showScalarBranding) {
        this.pdf.addImage(
          coverLogoSrc,
          coverLogoFormat,
          TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
          pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - logoHeightMm / 2,
          TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
        );
      }

      // Draw a horizontal line to the right of the logo, aligned at the bottom of the logo, spanning to page margin
      const logoY = pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - logoHeightMm / 2;
      const logoBottomY = logoY + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM;
      // Without Scalar branding the rule takes over the logo's slot, starting at the margin.
      const lineXStart = showScalarBranding
        ? TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM +
          TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM +
          TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM
        : TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(255, 255, 255);
      this.pdf.line(lineXStart, logoBottomY, lineXEnd, logoBottomY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);

      // Legal disclaimer vertically centered in the band between the logo rule and the
      // page's bottom edge. The block is measured from the first line's cap height to the
      // last baseline so the visible gap above and below the text matches.
      if (isScalarOwnedValuation) {
        const fontSizePt = TEMPLATE_CONSTANTS.COVER_DISCLAIMER_FONT_SIZE_PT;
        this.pdf.setFont('Inter', 'normal');
        this.pdf.setFontSize(fontSizePt);
        this.pdf.setTextColor(255, 255, 255);
        const disclaimerLines: string[] = this.pdf.splitTextToSize(
          SCALAR_OWNED_COVER_DISCLAIMER,
          pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM * 2
        );
        const capHeightMm = ptToCapHeightMm(fontSizePt);
        const lineHeightMm = fontSizePt * 0.3528 * this.pdf.getLineHeightFactor();
        const blockHeightMm = capHeightMm + (disclaimerLines.length - 1) * lineHeightMm;
        const blockTopY = logoBottomY + (pageHeight - logoBottomY - blockHeightMm) / 2;
        const firstLineY = blockTopY + capHeightMm;
        this.pdf.text(disclaimerLines, TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM, firstLineY);
      }
    }

    const normalizedMeasurementDate = measurementDate ? extractDate({ dateSlug: measurementDate }) : null;
    const parsedMeasurementDate = normalizedMeasurementDate
      ? moment(normalizedMeasurementDate, ISO_DATE_FORMAT, true)
      : null;
    const hasValidMeasurementDate = Boolean(parsedMeasurementDate?.isValid());

    // Strict, zone-free date-only parse, so no timezone shift is possible.
    const parsedIssueDate = issueDate ? moment(issueDate, ISO_DATE_FORMAT, true) : null;
    const hasValidIssueDate = Boolean(parsedIssueDate?.isValid());

    const halfHeight = this.pdf.internal.pageSize.getHeight() / 2;
    const xPosition = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const coverTitleY = halfHeight - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = halfHeight + TEMPLATE_CONSTANTS.COVER_TEXT_SEPARATOR_MM;
    const measurementDateY = entityNameY + 14;
    const issueDateY = hasValidMeasurementDate
      ? measurementDateY + 10
      : halfHeight + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    this.pdf.setTextColor(isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255);

    // Color for accent elements (line and issue date): #328E65 if isFirmOwned, otherwise #31EDA9
    const accentColor = isFirmOwned ? [50, 142, 101] : [49, 237, 169];

    this.pdf.setFont('Inter', 'italic');
    this.pdf.setFontSize(16);

    // Draw a line to the right of the coverTitle, aligned at the bottom. With no
    // title the line starts at the margin and runs longer, filling the title's slot.
    let lineXStart = xPosition;
    let lineXEnd = xPosition + TEMPLATE_CONSTANTS.COVER_UNTITLED_LINE_LENGTH_MM;
    if (coverTitle) {
      this.pdf.text(coverTitle, xPosition, coverTitleY);
      lineXStart = xPosition + this.pdf.getTextWidth(coverTitle);
      lineXEnd = lineXStart + TEMPLATE_CONSTANTS.COVER_TITLE_LINE_LENGTH_MM;
    }
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    this.pdf.line(lineXStart, coverTitleY, lineXEnd, coverTitleY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    this.pdf.setFont('Neuton', 'light');
    this.pdf.setFontSize(40);
    this.pdf.text(`${entityName}`, xPosition, entityNameY);

    if (hasValidMeasurementDate && parsedMeasurementDate) {
      const measurementDateText = `Valuation Date: ${parsedMeasurementDate.format('MMMM D, YYYY')}`;
      this.pdf.setFont('Inter', 'normal');
      this.pdf.setFontSize(17.5);
      this.pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      this.pdf.text(measurementDateText, xPosition, measurementDateY);
    }

    // Omit the line rather than printing "UNDEFINED" on the cover.
    if (hasValidIssueDate && parsedIssueDate) {
      this.pdf.setFont('Inter', 'normal');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      this.pdf.text(
        `Issue date: ${parsedIssueDate.format('MMMM D, YYYY')}`.toUpperCase(),
        xPosition,
        issueDateY
      );
    }

    // Set page text color to #037DE8
    this.pdf.setTextColor(0, 3, 125, 232);
  }

  /**
   * Generate header template for a PDF page
   */
  public generateHeader(config: PdfExportConfig, pageTitle: string): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const { entityName, firmName, isFirmOwned, showScalarBranding = true } = config;
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const headerY = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;

    // Logo dimensions: 75px width, 25px height converted to mm
    const logoWidthMm = 75 / CONVERSION_RATIOS.MM_TO_PX; // ~19.84mm
    const logoHeightMm = 25 / CONVERSION_RATIOS.MM_TO_PX; // ~6.62mm

    // Position logo at left margin, vertically centered
    const logoX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const logoY = headerY - logoHeightMm / 2;

    // Left header slot: the page logo when one is configured, otherwise the firm name
    // for firm-owned valuations. Never Scalar's logo -- branding a firm-owned valuation
    // with it would misattribute the work. Measure what is actually drawn so the divider
    // line below starts after it.
    let leftSlotWidthMm = 0;
    if (this.pageLogo && showScalarBranding) {
      this.pdf.addImage(
        this.pageLogo,
        PdfExporter.getImageFormat(this.pageLogo),
        logoX,
        logoY,
        logoWidthMm,
        logoHeightMm
      );
      leftSlotWidthMm = logoWidthMm;
    } else if (isFirmOwned && firmName) {
      // Baseline-aligned with the entity name on the right so both ends of the header
      // sit on the divider line.
      this.pdf.setFont('Inter', 'bold');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(3, 125, 232); // #037DE8
      const firmNameText = firmName.toUpperCase();
      this.pdf.text(firmNameText, logoX, headerY);
      leftSlotWidthMm = this.pdf.getTextWidth(firmNameText);
    }

    // Set font properties for entityName measurement: Inter, Bold (700), 10px
    // Try to use Inter Bold, fallback to Inter Regular if not available

    this.pdf.setFont('Inter', 'bold');
    this.pdf.setFontSize(10);

    // Text transform: uppercase
    const entityNameText = (entityName || '').toUpperCase();
    const entityNameWidth = this.pdf.getTextWidth(entityNameText);

    // Entity name positioned at right margin
    const entityNameSpacingMm = 3; // Space between line and entityName
    const entityNameX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = headerY;

    // Line after the left slot, ending before entityName
    const lineSpacingMm = 3; // Space between the left slot and the line
    // Without Scalar branding the line takes over the empty left slot, starting at the margin.
    const lineXStart = showScalarBranding ? logoX + leftSlotWidthMm + lineSpacingMm : logoX;
    const lineXEnd = entityNameX - entityNameSpacingMm - entityNameWidth;
    const lineY = headerY;

    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(3, 125, 232); // #037DE8
    // The left slot is variable width (a long firm name can reach the entity name), so
    // skip the line rather than drawing it backwards.
    if (lineXEnd > lineXStart) {
      this.pdf.line(lineXStart, lineY, lineXEnd, lineY);
    }
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Draw entity name
    this.pdf.setTextColor(3, 125, 232); // #037DE8
    this.pdf.text(entityNameText, entityNameX, entityNameY, { align: 'right' });

    // Add page title below the header
    const titleSpacingBelowHeaderMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;
    const titleY = headerY + titleSpacingBelowHeaderMm;
    this.pdf.setFont('Neuton', 'italic');
    this.pdf.setFontSize(20);
    this.pdf.text(pageTitle || '', TEMPLATE_CONSTANTS.PAGE_MARGIN_MM, titleY, { align: 'left' });

    // Draw line below page title
    const titleLineWidthPx = 50;
    const titleLineHeightPx = 2;
    const titleLineWidthMm = titleLineWidthPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineHeightMm = titleLineHeightPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineSpacingMm = 2; // Space between title and line
    const titleLineY = titleY + titleLineSpacingMm;
    const titleLineX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    this.pdf.setLineWidth(titleLineHeightMm);
    this.pdf.setDrawColor(49, 237, 169); // #31EDA9
    this.pdf.line(titleLineX, titleLineY, titleLineX + titleLineWidthMm, titleLineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Reset text color
    this.pdf.setTextColor(0, 0, 0);
  }

  // Width (mm) of the slot reserved for the "PAGE X OF Y" label. The footer's middle section
  // lays out from this slot's left edge, and Inter's figures are proportional, so measuring
  // the label actually being drawn would slide that section from page to page. Reserving the
  // widest label the export can produce keeps it pinned.
  // Assumes the caller has already selected the footer font and size.
  private measurePageNumberSlotWidth(totalPages: number): number {
    if (!this.pdf) {
      return 0;
    }
    const { pdf } = this;
    const widestDigit = '0123456789'
      .split('')
      .reduce((widest, digit) => (pdf.getTextWidth(digit) > pdf.getTextWidth(widest) ? digit : widest));
    // The page index never has more digits than the total, so this is a true upper bound.
    const widestPageIndex = widestDigit.repeat(String(totalPages).length);
    return pdf.getTextWidth(`PAGE ${widestPageIndex} OF ${totalPages}`);
  }

  /**
   * Generate footer template for a PDF page
   * Layout: line + status box + line + page number info, or line + page number info
   * when showStatus is false.
   */
  public generateFooter(
    currentPage: number,
    totalPages: number,
    status: number | undefined,
    showStatus = true
  ): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const pageHeight = this.pdf.internal.pageSize.getHeight();

    // Footer positioned at the bottom of the page
    const footerYMm = pageHeight - 15;
    const footerBaselineY = footerYMm;

    // Color constants
    const blueColor = [3, 125, 232]; // #037DE8

    // Determine status box background color based on status
    // Convert hex with alpha to RGB (approximate with lighter colors)
    let statusBoxBgColor: number[];

    if (status === 0) {
      // PUBLISHED: #19C2E71A (cyan/blue with alpha)
      statusBoxBgColor = [230, 248, 255]; // Light cyan to approximate rgba(25, 194, 231, 0.1)
    } else if (status === 1) {
      // FINAL: no fill
      statusBoxBgColor = [255, 255, 255];
    } else {
      // DRAFT: #7777771A (grey with alpha)
      statusBoxBgColor = [245, 245, 245]; // Light grey to approximate rgba(119, 119, 119, 0.1)
    }

    // Text color is the same for all statuses
    const statusTextColor = [0, 74, 139]; // #004A8B (dark blue)

    // Font settings - all text uses same font and size
    const fontSize = 10;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);

    const elementSpacing = 3; // Space between elements

    // Determine status text
    let statusText = 'DRAFT - SUBJECT TO APPROVAL';

    if (status === 0) {
      // ALLOCATIONS_PUBLISHED
      statusText = 'DRAFT - SUBJECT TO APPROVAL';
    }
    if (status === 1) {
      // ALLOCATIONS_FINAL
      statusText = 'FINAL VALUATION SUMMARY';
    }

    // Calculate status box dimensions
    // Height: 24px converted to mm
    const statusBoxHeightMm = 24 / CONVERSION_RATIOS.MM_TO_PX; // Approximately 6.35mm

    const iconSize = 2.5; // Icon size in mm
    const iconPadding = 1.5; // Padding around icon
    const statusBoxPadding = 2; // Padding inside the box (horizontal)

    // Calculate text width for status text
    const statusTextWidth = this.pdf.getTextWidth(statusText);

    // Determine if icon should be shown (only for draft)
    const showIcon = status === -1 || status === undefined;

    // Calculate content width (icon + spacing + text)
    const contentWidth = showIcon ? iconSize + iconPadding + statusTextWidth : statusTextWidth;

    // Status box width = content + padding on both sides
    const statusBoxWidth = contentWidth + statusBoxPadding * 2;

    // Calculate page number text and position first to determine line positions
    const pageNumberText = `PAGE ${currentPage} OF ${totalPages}`;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
    // Page-invariant slot, not this page's own label — see measurePageNumberSlotWidth.
    // The label is still drawn right-aligned at pageNumberX, so its right edge stays put.
    const pageNumberSlotWidth = this.measurePageNumberSlotWidth(totalPages);
    const pageNumberX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const pageNumberY = footerBaselineY;

    const lineGap = elementSpacing; // Gap between each line and the status box

    // The footer middle section spans from the left margin to just before the
    // page number. Center the status box within that span.
    const spanStartX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const spanEndX = pageNumberX - pageNumberSlotWidth - elementSpacing;

    if (!showStatus) {
      // One solid line across the whole span, then the page number.
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(blueColor[0], blueColor[1], blueColor[2]);
      if (spanEndX > spanStartX) {
        this.pdf.line(spanStartX, footerBaselineY, spanEndX, footerBaselineY);
      }
      this.pdf.text(pageNumberText, pageNumberX, pageNumberY, { align: 'right' });

      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);
      this.pdf.setTextColor(0, 0, 0);
      return;
    }

    const statusBoxCenterX = (spanStartX + spanEndX) / 2;
    const statusBoxX = statusBoxCenterX - statusBoxWidth / 2;
    const statusBoxY = footerBaselineY - statusBoxHeightMm / 2;

    // Anchor the lines to the status box edges so they never overlap the box or
    // its text, and let them run out to the span edges to fill the band.
    const line1StartX = spanStartX;
    const line1EndX = statusBoxX - lineGap;

    const line2StartX = statusBoxX + statusBoxWidth + lineGap;
    const line2EndX = spanEndX;

    // 1. Draw first blue line (from the left margin, up to the status box)
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(blueColor[0], blueColor[1], blueColor[2]);
    if (line1EndX > line1StartX) {
      this.pdf.line(line1StartX, footerBaselineY, line1EndX, footerBaselineY);
    }

    // 2. Draw status box with background
    // Draw background rectangle with rounded corners
    this.pdf.setFillColor(statusBoxBgColor[0], statusBoxBgColor[1], statusBoxBgColor[2]);
    this.pdf.roundedRect(statusBoxX, statusBoxY, statusBoxWidth, statusBoxHeightMm, 1.5, 1.5, 'F');

    // Calculate centered positions for icon and text
    const contentStartX = statusBoxX + (statusBoxWidth - contentWidth) / 2; // Center content horizontally

    // Add info icon inside the box (only for draft, aligned with text baseline)
    if (showIcon && this.infoIcon) {
      const iconX = contentStartX;
      // Align icon center with text baseline (footerBaselineY)
      const iconY = footerBaselineY - iconSize / 2;
      this.pdf.addImage(this.infoIcon, IMAGE_SETTINGS.PNG_FORMAT, iconX, iconY, iconSize, iconSize);
    }

    // Add status text inside the box (centered horizontally, aligned with baseline)
    this.pdf.setTextColor(statusTextColor[0], statusTextColor[1], statusTextColor[2]);
    const statusTextX = showIcon ? contentStartX + iconSize + iconPadding : contentStartX;
    // Text baseline should align with footerBaselineY for proper vertical centering
    const statusTextY = footerBaselineY + 1.5;
    this.pdf.text(statusText, statusTextX, statusTextY);

    // 3. Draw second blue line (from the status box, toward the page number)
    if (line2EndX > line2StartX) {
      this.pdf.line(line2StartX, footerBaselineY, line2EndX, footerBaselineY);
    }

    // 4. Draw page number info on the right
    this.pdf.text(pageNumberText, pageNumberX, pageNumberY, { align: 'right' });

    // Reset drawing settings
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);
    this.pdf.setTextColor(0, 0, 0);
  }

  /**
   * Phase 1 of streaming export: create the JsPDF instance, load fonts and
   * static logos, and render the cover. Must be called before addCapturedPage.
   */
  public async initDocument(config: PdfExportConfig): Promise<void> {
    this.renderConfig = config;
    this.addedPages = [];

    this.pdf = new JsPDF({
      format: 'letter',
      orientation: 'landscape',
      compress: true,
    });

    await PdfExporter.loadCustomFonts(this.pdf);

    // Load each image independently so a single failure does not prevent the
    // others from being available. All fields default to null and callers fall
    // back to the raw source path when null.
    const loadResult = await Promise.allSettled([
      PdfExporter.loadOptionalImageAsDataUrl(config.pageLogo),
      PdfExporter.loadOptionalImageAsDataUrl(config.coverLogo),
      PdfExporter.loadOptionalImageAsDataUrl(config.infoIcon),
    ]);
    if (loadResult[0].status === 'fulfilled') this.pageLogo = loadResult[0].value;
    else Logger.warn('Failed loading pageLogo for PDF export');
    if (loadResult[1].status === 'fulfilled') this.coverLogoDataUrl = loadResult[1].value;
    else Logger.warn('Failed loading coverLogo for PDF export');
    if (loadResult[2].status === 'fulfilled') this.infoIcon = loadResult[2].value;
    else Logger.warn('Failed loading infoIcon for PDF export');

    await this.generateCover(config);
  }

  /**
   * Phase 2 of streaming export: add a single captured page to the PDF.
   * Footer rendering is deferred to finalize() because the "Page X of Y"
   * stamp needs the final included-page count.
   *
   * The image's Blob is converted to a data URL only at the moment of
   * addImage, then dropped. This keeps peak heap usage to a single decoded
   * image, regardless of how many pages have been captured.
   */
  public async addCapturedPage(link: PdfPage, image: CapturedImage, footer?: ExportFooterData): Promise<void> {
    if (!this.pdf || !this.renderConfig) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    if (image.width <= 0 || image.height <= 0) {
      Logger.warn(`addCapturedPage: skipping ${link.exportTarget} — image has zero dimensions`);
      return;
    }

    const config = this.renderConfig;
    const pageNumberInExport = this.addedPages.length + 1;

    this.pdf.addPage();

    this.pdf.addImage(
      config.pageBackgroundImage,
      IMAGE_SETTINGS.PNG_FORMAT,
      0,
      0,
      this.pdf.internal.pageSize.getWidth(),
      this.pdf.internal.pageSize.getHeight(),
      'page-background-image'
    );

    this.generateHeader(config, link.title);

    // Decode the Blob only at the moment of addImage, so the data URL string
    // exists transiently rather than accumulating across all captures.
    const contentImgData = await PdfExporter.blobToDataUrl(image.blob);
    const contentImgFormat = image.format;

    const canvasWidthMm = image.width / CONVERSION_RATIOS.MM_TO_PX;
    const canvasHeightMm = image.height / CONVERSION_RATIOS.MM_TO_PX;

    const contentYPositionMm = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    // When the page carries footer notes/documents (drawn as text below the image by
    // renderFooterData), reserve vertical room for them so they share this page instead of
    // being pushed onto a continuation page. Without this, a tall capture fills the full
    // MAX_HEIGHT_MM and leaves no room, forcing every notes block onto its own page.
    const hasFooter = Boolean(footer && (footer.notes.length > 0 || footer.documents.length > 0));
    // Mirror renderFooterData's geometry: text must end above the stamped footer band.
    const footerBottomLimitMm = this.pdf.internal.pageSize.getHeight() - FOOTER_DATA_LAYOUT.TEXT_BOTTOM_CLEARANCE_MM;
    const footerMm = hasFooter ? this.measureFooterDataHeight(footer as ExportFooterData) : 0;
    let effectiveMaxHeightMm: number = IMAGE_SETTINGS.MAX_HEIGHT_MM;
    if (hasFooter) {
      const usableHeightMm = footerBottomLimitMm - contentYPositionMm;
      // Never let the footer starve the image below a floor; footers larger than the
      // remaining space overflow onto continuation pages exactly as before.
      const minImageHeightMm = usableHeightMm * FOOTER_DATA_LAYOUT.MIN_IMAGE_HEIGHT_FRACTION;
      // Match the bottom-pinned path's slack. Reserving the bare estimate puts `naturalStartY`
      // past `pinnedStartY`, and rounding then spills the notes block onto its own page.
      const reservedFooterMm = Math.min(
        footerMm + FOOTER_DATA_LAYOUT.BOTTOM_PIN_SAFETY_MM,
        usableHeightMm - minImageHeightMm
      );
      effectiveMaxHeightMm = Math.max(minImageHeightMm, usableHeightMm - reservedFooterMm);
    }

    // html2canvas captures at scale:2, so 0.5 maps back to 1 CSS px = 1 PDF unit at 96 DPI.
    // Capping at 0.5 prevents small tables (e.g. External Valuation) from being upscaled
    // beyond their natural browser size while leaving large downscaled tables unchanged.
    const NATURAL_SCALE_CAP = 0.5;
    const contentScale = Math.min(
      IMAGE_SETTINGS.MAX_WIDTH_MM / canvasWidthMm,
      effectiveMaxHeightMm / canvasHeightMm,
      NATURAL_SCALE_CAP
    );
    const contentWidthMm = canvasWidthMm * contentScale;
    const contentHeightMm = canvasHeightMm * contentScale;

    const dimensionsAreValid =
      Number.isFinite(contentWidthMm) && Number.isFinite(contentHeightMm) && contentWidthMm > 0 && contentHeightMm > 0;

    const contentXPositionMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM + (IMAGE_SETTINGS.MAX_WIDTH_MM - contentWidthMm) / 2;

    let contentAdded = false;
    if (dimensionsAreValid) {
      try {
        this.pdf.addImage(
          contentImgData,
          contentImgFormat,
          contentXPositionMm,
          contentYPositionMm,
          contentWidthMm,
          contentHeightMm,
          IMAGE_SETTINGS.PDF_ALIAS,
          IMAGE_SETTINGS.COMPRESSION
        );
        contentAdded = true;
        Logger.info(`Added content to page ${pageNumberInExport}`);
      } catch (imgError) {
        Logger.warn(`addImage failed on page ${pageNumberInExport}; falling back to placeholder`, imgError);
      }
    } else {
      Logger.warn(
        `Skipping addImage on page ${pageNumberInExport}: computed dimensions invalid ` +
          `(w=${contentWidthMm}, h=${contentHeightMm}, image=${image.width}x${image.height})`
      );
    }

    if (!contentAdded) {
      this.pdf.setFont('Inter', 'italic');
      this.pdf.setFontSize(14);
      this.pdf.setTextColor(128, 128, 128);
      this.pdf.text(
        'No data available',
        TEMPLATE_CONSTANTS.PAGE_MARGIN_MM,
        TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2
      );
      this.pdf.setTextColor(0, 0, 0);
    }

    // Record the captured page before rendering the footer so continuation
    // pages (added when the notes/documents text overflows) are appended after
    // it, keeping addedPages in PDF page order for footer stamping.
    this.addedPages.push({ link });

    if (footer && (footer.notes.length > 0 || footer.documents.length > 0)) {
      const naturalStartY = contentAdded
        ? contentYPositionMm + contentHeightMm
        : TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM +
          TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2 +
          FOOTER_DATA_LAYOUT.CONTENT_GAP_MM;
      // Pin the notes/documents block to the bottom of the page: start it so its
      // last line ends just above the footer clearance line instead of hugging a
      // small exhibit. When the block is too tall to fit below the image (pinned
      // start would sit above the natural start), fall back to the natural start
      // so it flows onto continuation pages exactly as before.
      const pinnedStartY = footerBottomLimitMm - footerMm - FOOTER_DATA_LAYOUT.BOTTOM_PIN_SAFETY_MM;
      const footerStartY = Math.max(naturalStartY, pinnedStartY);
      this.renderFooterData(link, footer, footerStartY);
    }
  }

  /**
   * Estimates the vertical height (in mm) that renderFooterData will consume for
   * a given footer, including the leading CONTENT_GAP_MM. Used by addCapturedPage
   * to shrink the captured image enough that the notes/documents share the page.
   *
   * Keep this in sync with renderFooterData below: it mirrors the same fonts,
   * sizes, wrapping width, headings, and gaps but advances a counter instead of
   * drawing. It does not model continuation-page breaks (the caller floors the
   * reserved space, and long footers overflow onto continuation pages as before).
   */
  private measureFooterDataHeight(footer: ExportFooterData): number {
    if (!this.pdf) {
      return 0;
    }
    const maxWidth = IMAGE_SETTINGS.MAX_WIDTH_MM;
    const hasNotes = footer.notes.length > 0;
    const hasDocuments = footer.documents.length > 0;

    // When both sections are present they are laid out side by side, so the block
    // height is the taller of the two columns (not their sum). Measure each column
    // at the column width so wrapping matches renderFooterData's two-column path.
    if (hasNotes && hasDocuments) {
      const columnWidth = (maxWidth - FOOTER_DATA_LAYOUT.COLUMN_GAP_MM) / 2;
      return (
        FOOTER_DATA_LAYOUT.CONTENT_GAP_MM +
        Math.max(
          this.measureFooterSectionHeight('notes', footer, columnWidth),
          this.measureFooterSectionHeight('documents', footer, columnWidth)
        )
      );
    }

    // A single section takes the full content width and stacks as before.
    let height = FOOTER_DATA_LAYOUT.CONTENT_GAP_MM;
    if (hasNotes) {
      height += this.measureFooterSectionHeight('notes', footer, maxWidth);
    }
    if (hasDocuments) {
      height += this.measureFooterSectionHeight('documents', footer, maxWidth);
    }
    return height;
  }

  /**
   * Estimates the height (in mm) of a single footer section (Notes or Documents)
   * when wrapped to the given width, including its heading but excluding the
   * leading CONTENT_GAP_MM and inter-section gaps. Shared by
   * measureFooterDataHeight (space reservation) and kept in sync with the drawing
   * done by renderFooterColumn / renderFooterData.
   */
  private measureFooterSectionHeight(kind: 'notes' | 'documents', footer: ExportFooterData, width: number): number {
    if (!this.pdf) {
      return 0;
    }
    const { pdf } = this;
    const headingHeightMm = ptToLineHeightMm(FOOTER_DATA_LAYOUT.HEADING_FONT_PT) + 1.5;

    const measureWrapped = (text: string, bold: boolean, size: number, indent: number): number => {
      pdf.setFont('Inter', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      const lines: string[] = pdf.splitTextToSize(text, width - indent);
      return lines.length * ptToLineHeightMm(size);
    };

    let height = headingHeightMm;

    if (kind === 'notes') {
      footer.notes.forEach((note, index) => {
        if (index > 0) {
          height += FOOTER_DATA_LAYOUT.NOTE_ITEM_GAP_MM;
        }
        if (note.name) {
          height += measureWrapped(note.name, true, FOOTER_DATA_LAYOUT.NOTE_NAME_FONT_PT, 0);
        }
        if (note.text) {
          height += measureWrapped(note.text, false, FOOTER_DATA_LAYOUT.BODY_FONT_PT, 0);
        }
      });
    } else {
      footer.documents.forEach(name => {
        height += measureWrapped(`•  ${name}`, false, FOOTER_DATA_LAYOUT.BODY_FONT_PT, 2);
      });
    }

    return height;
  }

  /**
   * Draws the page's client notes and document names as real (selectable) PDF
   * text below the captured image. Overflowing content flows onto continuation
   * pages so it never collides with the stamped footer band. Each continuation
   * page is recorded in addedPages so finalize() stamps its footer and the
   * "Page X of Y" count stays correct.
   *
   * Keep the vertical math (fonts, sizes, headings, gaps) in sync with
   * measureFooterDataHeight above, which estimates this layout's height.
   */
  private renderFooterData(link: PdfPage, footer: ExportFooterData, startY: number): void {
    if (!this.pdf || !this.renderConfig) {
      return;
    }
    const { pdf } = this;
    const config = this.renderConfig;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const leftX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const maxWidth = IMAGE_SETTINGS.MAX_WIDTH_MM;
    // Start text well below the header/title area: generateHeader draws the
    // page title and its green underline a few mm below HEADER_TOTAL_HEIGHT_MM,
    // so an extra page-margin of clearance keeps the text from crowding it
    // (matters most on continuation pages, where text — not a screenshot with
    // its own top whitespace — sits directly under the title).
    const topY = TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;
    const bottomLimit = pageHeight - FOOTER_DATA_LAYOUT.TEXT_BOTTOM_CLEARANCE_MM;

    // Two-column layout: when both Notes and Documents are present, place Notes in
    // the left column and Documents in the right column instead of stacking them.
    // Only when the taller column fits within the page band below the image do we
    // use columns; otherwise we fall through to the single-column stacked flow,
    // which handles continuation-page overflow. measureFooterDataHeight mirrors
    // this decision so the reserved/pinned space matches what we draw here.
    if (footer.notes.length > 0 && footer.documents.length > 0) {
      const columnWidth = (maxWidth - FOOTER_DATA_LAYOUT.COLUMN_GAP_MM) / 2;
      const startTop = Math.max(startY + FOOTER_DATA_LAYOUT.CONTENT_GAP_MM, topY);
      const blockHeight = Math.max(
        this.measureFooterSectionHeight('notes', footer, columnWidth),
        this.measureFooterSectionHeight('documents', footer, columnWidth)
      );
      if (startTop + blockHeight <= bottomLimit) {
        const rightColX = leftX + columnWidth + FOOTER_DATA_LAYOUT.COLUMN_GAP_MM;
        this.renderFooterColumn('notes', footer, leftX, columnWidth, startTop);
        this.renderFooterColumn('documents', footer, rightColX, columnWidth, startTop);
        pdf.setTextColor(0, 0, 0);
        return;
      }
    }

    let y = Math.max(startY + FOOTER_DATA_LAYOUT.CONTENT_GAP_MM, topY);

    const startContinuationPage = () => {
      pdf.addPage();
      pdf.addImage(
        config.pageBackgroundImage,
        IMAGE_SETTINGS.PNG_FORMAT,
        0 /* x */,
        0 /* y */,
        pageWidth,
        pageHeight,
        'page-background-image'
      );
      this.generateHeader(config, link.title);
      this.addedPages.push({ link });
      y = topY;
    };

    const ensureSpace = (needed: number) => {
      if (y + needed > bottomLimit) {
        startContinuationPage();
      }
    };

    // Orphan control: reserve room for the heading plus the first line of its
    // following content so a section heading is never left alone at the bottom
    // of a page (the break happens before the heading instead).
    const writeHeading = (text: string, firstLineHeightMm: number) => {
      const headingPt = FOOTER_DATA_LAYOUT.HEADING_FONT_PT;
      // The 1.5 must match both the advance below and measureFooterSectionHeight's heading
      // height, or ensureSpace breaks a block addCapturedPage already proved would fit.
      ensureSpace(ptToLineHeightMm(headingPt) + 1.5 + firstLineHeightMm);
      pdf.setFont('Inter', 'bold');
      pdf.setFontSize(headingPt);
      pdf.setTextColor(3, 125, 232);
      pdf.text(text, leftX, y);
      y += ptToLineHeightMm(headingPt) + 1.5;
      pdf.setTextColor(0, 0, 0);
    };

    const applyBodyFont = (bold: boolean, size: number) => {
      pdf.setFont('Inter', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      pdf.setTextColor(0, 0, 0);
    };

    const writeWrapped = (
      text: string,
      {
        bold = false,
        bullet = false,
        indent = 0,
        size = 9,
      }: { bold?: boolean; bullet?: boolean; indent?: number; size?: number }
    ) => {
      applyBodyFont(bold, size);
      const lineHeight = ptToLineHeightMm(size);
      const prefix = bullet ? '•  ' : '';
      const lines: string[] = pdf.splitTextToSize(prefix + text, maxWidth - indent);
      lines.forEach(line => {
        ensureSpace(lineHeight);
        // Re-apply the body font after ensureSpace: a continuation page break
        // runs generateHeader(), which leaves the title font (Neuton 20pt)
        // active and would otherwise render this line in the wrong, larger font.
        applyBodyFont(bold, size);
        pdf.text(line, leftX + indent, y);
        y += lineHeight;
      });
    };

    if (footer.notes.length > 0) {
      const firstNote = footer.notes[0];
      const firstNoteLineHeightMm = ptToLineHeightMm(
        firstNote?.name ? FOOTER_DATA_LAYOUT.NOTE_NAME_FONT_PT : FOOTER_DATA_LAYOUT.BODY_FONT_PT
      );
      writeHeading('Notes', firstNoteLineHeightMm);
      footer.notes.forEach((note, index) => {
        if (index > 0) {
          y += FOOTER_DATA_LAYOUT.NOTE_ITEM_GAP_MM;
        }
        if (note.name) {
          writeWrapped(note.name, { bold: true, size: FOOTER_DATA_LAYOUT.NOTE_NAME_FONT_PT });
        }
        if (note.text) {
          writeWrapped(note.text, { size: FOOTER_DATA_LAYOUT.BODY_FONT_PT });
        }
      });
      y += FOOTER_DATA_LAYOUT.NOTES_SECTION_TRAILING_GAP_MM;
    }

    if (footer.documents.length > 0) {
      writeHeading('Documents', ptToLineHeightMm(FOOTER_DATA_LAYOUT.BODY_FONT_PT));
      footer.documents.forEach(name =>
        writeWrapped(name, { bullet: true, indent: 2, size: FOOTER_DATA_LAYOUT.BODY_FONT_PT })
      );
    }

    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Draws a single footer section (Notes or Documents) as a self-contained
   * column starting at (colX, startTop) and wrapping to colWidth. Used by the
   * two-column footer layout, which places Notes and Documents side by side.
   * Unlike renderFooterData's single-column flow, a column never spills onto a
   * continuation page — the caller only chooses this layout when the taller
   * column fits within the page band (see renderFooterData). Keep the fonts,
   * sizes, headings, and gaps in sync with measureFooterSectionHeight.
   */
  private renderFooterColumn(
    kind: 'notes' | 'documents',
    footer: ExportFooterData,
    colX: number,
    colWidth: number,
    startTop: number
  ): void {
    if (!this.pdf) {
      return;
    }
    const { pdf } = this;
    let y = startTop;

    const writeHeading = (text: string) => {
      const headingPt = FOOTER_DATA_LAYOUT.HEADING_FONT_PT;
      pdf.setFont('Inter', 'bold');
      pdf.setFontSize(headingPt);
      pdf.setTextColor(3, 125, 232);
      pdf.text(text, colX, y);
      y += ptToLineHeightMm(headingPt) + 1.5;
      pdf.setTextColor(0, 0, 0);
    };

    const writeWrapped = (
      text: string,
      {
        bold = false,
        bullet = false,
        indent = 0,
        size = 9,
      }: { bold?: boolean; bullet?: boolean; indent?: number; size?: number }
    ) => {
      pdf.setFont('Inter', bold ? 'bold' : 'normal');
      pdf.setFontSize(size);
      pdf.setTextColor(0, 0, 0);
      const lineHeight = ptToLineHeightMm(size);
      const prefix = bullet ? '•  ' : '';
      const lines: string[] = pdf.splitTextToSize(prefix + text, colWidth - indent);
      lines.forEach(line => {
        pdf.text(line, colX + indent, y);
        y += lineHeight;
      });
    };

    if (kind === 'notes') {
      writeHeading('Notes');
      footer.notes.forEach((note, index) => {
        if (index > 0) {
          y += FOOTER_DATA_LAYOUT.NOTE_ITEM_GAP_MM;
        }
        if (note.name) {
          writeWrapped(note.name, { bold: true, size: FOOTER_DATA_LAYOUT.NOTE_NAME_FONT_PT });
        }
        if (note.text) {
          writeWrapped(note.text, { size: FOOTER_DATA_LAYOUT.BODY_FONT_PT });
        }
      });
    } else {
      writeHeading('Documents');
      footer.documents.forEach(name =>
        writeWrapped(name, { bullet: true, indent: 2, size: FOOTER_DATA_LAYOUT.BODY_FONT_PT })
      );
    }

    pdf.setTextColor(0, 0, 0);
  }

  /**
   * Phase 3 of streaming export: stamp footers (with final "Page X of Y"
   * counts), output the blob, and optionally trigger the browser download.
   */
  public async finalize(filename: string, triggerDownload: boolean): Promise<PdfExportResult> {
    if (!this.pdf || !this.renderConfig) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const config = this.renderConfig;
    const totalIncludedPages = this.addedPages.length;

    Logger.info(`Finalizing PDF with ${totalIncludedPages} page(s) of content`);

    // Two-pass footer stamping. PDF page 1 is the cover; captured pages start
    // at PDF page 2. setPage(n) activates page n for subsequent drawing.
    for (let i = 0; i < totalIncludedPages; i++) {
      this.pdf.setPage(i + 2);
      this.generateFooter(i + 1, totalIncludedPages, config.allocationStatus, config.showFooterStatus !== false);
    }

    const safeFilename = filename || 'pdf-export';
    const pdfBlob = this.pdf.output('blob');

    if (triggerDownload) {
      Logger.info(`Downloading final PDF: ${safeFilename}.pdf`);
      try {
        this.pdf.save(`${safeFilename}.pdf`);
      } catch (saveError) {
        Logger.error('Error calling finalPdf.save():', saveError);
      }
    }

    return {
      success: true,
      filename: `${safeFilename}.pdf`,
      totalPages: totalIncludedPages,
      processedPages: totalIncludedPages,
      pdfBlob,
    };
  }

  private static blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
